import type { PublicQuoteLineItem } from '../_types/quote'

interface QuoteTotals {
  subtotal: number
  discountAmount: number
  taxRatePercent: number
  taxAmount: number
  total: number
}

/**
 * Rounds a currency value to two decimal places, guarding against binary
 * floating point drift.
 *
 * @param {number} value - The raw monetary value.
 * @returns {number} The value rounded to cents.
 */
const roundToCents = (value: number): number =>
  Math.round((value + Number.EPSILON) * 100) / 100

/**
 * Returns the line item ids selected by default (before the recipient makes a
 * choice): every required item plus every non-required item flagged
 * `defaultSelected`, keeping at most one default per choose-one group. Mirrors
 * the server helper.
 *
 * @param {PublicQuoteLineItem[]} lineItems - The quote line items.
 * @returns {string[]} The default-selected line item ids.
 */
const getInitialSelectedLineItemIds = (
  lineItems: PublicQuoteLineItem[],
): string[] => {
  const claimedGroups = new Set<string>()
  const selected: string[] = []

  lineItems.forEach((lineItem) => {
    const wantsSelection =
      (lineItem.selectionMode !== 'optional' &&
        lineItem.selectionMode !== 'choose-one') ||
      lineItem.defaultSelected === true

    if (!wantsSelection) {
      return
    }

    if (lineItem.selectionMode === 'choose-one' && lineItem.optionGroup) {
      if (claimedGroups.has(lineItem.optionGroup)) {
        return
      }
      claimedGroups.add(lineItem.optionGroup)
    }

    selected.push(lineItem.id)
  })

  return selected
}

/**
 * Resolves which line items are actually included given the recipient's intended
 * selections, honouring required/optional/choose-one and `requiresAnyOf`
 * prerequisites to a fixed point. Mirrors the server helper.
 *
 * @param {PublicQuoteLineItem[]} lineItems - The quote line items.
 * @param {string[]} intendedIds - The ids the recipient intends to select.
 * @returns {string[]} The ids of the line items that are actually included.
 */
const resolveIncludedLineItems = (
  lineItems: PublicQuoteLineItem[],
  intendedIds: string[],
): string[] => {
  const intended = new Set(intendedIds)
  const wants = new Map<string, boolean>()

  lineItems.forEach((lineItem) => {
    wants.set(
      lineItem.id,
      (lineItem.selectionMode !== 'optional' &&
        lineItem.selectionMode !== 'choose-one') ||
        intended.has(lineItem.id),
    )
  })

  const claimedGroups = new Set<string>()
  lineItems.forEach((lineItem) => {
    if (
      lineItem.selectionMode !== 'choose-one' ||
      !lineItem.optionGroup ||
      wants.get(lineItem.id) !== true
    ) {
      return
    }

    if (claimedGroups.has(lineItem.optionGroup)) {
      wants.set(lineItem.id, false)
    } else {
      claimedGroups.add(lineItem.optionGroup)
    }
  })

  const included = new Set<string>()
  let hasChanged = true

  while (hasChanged) {
    hasChanged = false
    for (const lineItem of lineItems) {
      const prerequisites = lineItem.requiresAnyOf ?? []
      const prerequisitesMet =
        prerequisites.length === 0 ||
        prerequisites.some((prerequisiteId) => included.has(prerequisiteId))
      const shouldInclude = wants.get(lineItem.id) === true && prerequisitesMet

      if (shouldInclude && !included.has(lineItem.id)) {
        included.add(lineItem.id)
        hasChanged = true
      }
      if (!shouldInclude && included.has(lineItem.id)) {
        included.delete(lineItem.id)
        hasChanged = true
      }
    }
  }

  return lineItems
    .filter((lineItem) => included.has(lineItem.id))
    .map((lineItem) => lineItem.id)
}

/**
 * Computes the signed monetary amount of each line item. A charge is
 * quantity × unit price; a discount line is negative, computed as a flat value
 * or a percentage of its base (the whole charge subtotal, or a single targeted
 * charge line), capped at that base. Mirrors the server helper.
 *
 * @param {PublicQuoteLineItem[]} lineItems - The included line items (charges + discounts).
 * @returns {Map<string, number>} A map of line item id to signed amount.
 */
const buildLineItemAmounts = (
  lineItems: PublicQuoteLineItem[],
): Map<string, number> => {
  const chargeAmounts = new Map<string, number>()
  let chargeSubtotal = 0

  lineItems.forEach((lineItem) => {
    if (lineItem.kind === 'discount') {
      return
    }
    const quantity = Number.isFinite(lineItem.quantity) ? lineItem.quantity : 0
    const unitPrice = Number.isFinite(lineItem.unitPrice)
      ? lineItem.unitPrice
      : 0
    const amount = quantity * unitPrice
    chargeAmounts.set(lineItem.id, amount)
    chargeSubtotal += amount
  })

  const amounts = new Map<string, number>()
  lineItems.forEach((lineItem) => {
    if (lineItem.kind !== 'discount') {
      amounts.set(lineItem.id, chargeAmounts.get(lineItem.id) ?? 0)
      return
    }
    const base = lineItem.discountAppliesTo
      ? (chargeAmounts.get(lineItem.discountAppliesTo) ?? 0)
      : chargeSubtotal
    const value = Number.isFinite(lineItem.discountValue)
      ? (lineItem.discountValue as number)
      : 0
    const raw =
      lineItem.discountMode === 'percent' ? (base * value) / 100 : value
    const magnitude = Math.max(0, Math.min(raw, base))
    amounts.set(lineItem.id, -magnitude)
  })

  return amounts
}

/**
 * Computes quote totals over the line items that resolve as included for the
 * recipient's intended selections. Discount lines reduce the subtotal; the flat
 * quote-wide discount applies before tax. Mirrors the server total calculation.
 *
 * @param {PublicQuoteLineItem[]} lineItems - The quote line items.
 * @param {string[]} intendedIds - The ids the recipient intends to select.
 * @param {number} discountAmount - A flat quote-wide discount applied to the subtotal.
 * @param {number} taxRatePercent - The tax rate as a percentage.
 * @returns {QuoteTotals} The computed totals over the included line items.
 */
const computeSelectedTotals = (
  lineItems: PublicQuoteLineItem[],
  intendedIds: string[],
  discountAmount: number,
  taxRatePercent: number,
): QuoteTotals => {
  const includedIds = new Set(resolveIncludedLineItems(lineItems, intendedIds))
  const includedItems = lineItems.filter((lineItem) =>
    includedIds.has(lineItem.id),
  )
  const amounts = buildLineItemAmounts(includedItems)
  let rawSubtotal = 0
  amounts.forEach((amount) => {
    rawSubtotal += amount
  })

  const subtotal = roundToCents(Math.max(0, rawSubtotal))
  const safeDiscount = Number.isFinite(discountAmount)
    ? Math.max(0, discountAmount)
    : 0
  const cappedDiscount = roundToCents(Math.min(safeDiscount, subtotal))
  const taxableBase = roundToCents(subtotal - cappedDiscount)
  const safeTaxRate = Number.isFinite(taxRatePercent)
    ? Math.max(0, taxRatePercent)
    : 0
  const taxAmount = roundToCents((taxableBase * safeTaxRate) / 100)
  const total = roundToCents(taxableBase + taxAmount)

  return {
    subtotal,
    discountAmount: cappedDiscount,
    taxRatePercent: safeTaxRate,
    taxAmount,
    total,
  }
}

export {
  getInitialSelectedLineItemIds,
  resolveIncludedLineItems,
  buildLineItemAmounts,
  computeSelectedTotals,
}
